/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Gervasi — All rights reserved.
 *
 * useSpeechInput.ts
 * Hook riutilizzabile per dettatura vocale.
 * Su Android usa VoskSpeech — riconoscimento offline, modello italiano incluso nell'APK.
 * Su browser desktop usa Web Speech API.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

interface VoskSpeechPlugin {
  initialize(): Promise<{ status: string }>;
  available(): Promise<{ available: boolean; modelReady: boolean }>;
  requestPermissions(): Promise<{ microphone: string }>;
  start(): Promise<void>;
  stop(): Promise<void>;
  addListener(event: 'partialResults',   handler: (data: { matches: string[] }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'listeningState',   handler: (data: { status: string })    => void): Promise<PluginListenerHandle>;
  addListener(event: 'recognitionError', handler: (data: { code: number })      => void): Promise<PluginListenerHandle>;
  addListener(event: 'modelState',       handler: (data: { status: string; message?: string }) => void): Promise<PluginListenerHandle>;
}

const VoskSpeech = registerPlugin<VoskSpeechPlugin>('VoskSpeech');

interface SpeechInputOptions {
  lang?: string;
  continuous?: boolean;
  onResult?: (text: string) => void;
  onError?: (error: string) => void;
}

interface SpeechInputState {
  isListening: boolean;
  isSupported: boolean;
  isModelReady: boolean;
  transcript: string;
  error: string | null;
}

const isNative = Capacitor.isNativePlatform();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SpeechRecognitionAPI: any =
  !isNative &&
  (typeof window !== 'undefined') &&
  ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

export function useSpeechInput(options: SpeechInputOptions = {}) {
  const {
    lang = 'it-IT',
    continuous = false,
    onResult,
    onError,
  } = options;

  const [state, setState] = useState<SpeechInputState>({
    isListening:  false,
    isSupported:  isNative || !!SpeechRecognitionAPI,
    isModelReady: !isNative,  // browser: sempre pronto; Android: attende Vosk
    transcript:   '',
    error:        null,
  });

  const recognitionRef = useRef<any>(null);
  const partialRef     = useRef<any>(null);
  const stateRef       = useRef<any>(null);
  const errorRef       = useRef<any>(null);
  const modelRef       = useRef<any>(null);
  const timeoutRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTextRef    = useRef<string>('');

  // Refs stabili per onResult/onError — evitano che le callback inline del parent
  // ricreino startListening/stopListening ad ogni render
  const onResultRef = useRef(onResult);
  const onErrorRef  = useRef(onError);
  onResultRef.current = onResult;
  onErrorRef.current  = onError;

  const cleanupNative = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    partialRef.current?.remove(); partialRef.current = null;
    stateRef.current?.remove();   stateRef.current   = null;
    errorRef.current?.remove();   errorRef.current   = null;
    lastTextRef.current = '';
  }, []);

  useEffect(() => {
    if (!isNative) return;

    VoskSpeech.requestPermissions();

    // Avvia esplicitamente il caricamento del modello
    VoskSpeech.initialize();

    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const checkReady = () => {
      VoskSpeech.available().then(res => {
        if (res.modelReady) {
          setState(s => ({ ...s, isModelReady: true, error: null }));
          if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        }
      });
    };

    // Controlla subito e poi ogni 3s finché non è pronto
    checkReady();
    pollTimer = setInterval(checkReady, 3000);

    // Listener eventi real-time (backup al polling)
    VoskSpeech.addListener('modelState', (data) => {
      if (data.status === 'ready') {
        setState(s => ({ ...s, isModelReady: true, error: null }));
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      } else if (data.status === 'error') {
        setState(s => ({ ...s, isModelReady: false, error: `Errore modello vocale: ${data.message ?? 'riavvia l\'app'}` }));
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      }
    }).then(h => { modelRef.current = h; });

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      cleanupNative();
      modelRef.current?.remove();
      VoskSpeech.stop();
    };
  }, [cleanupNative]);

  const startListening = useCallback(async () => {
    if (!state.isSupported) {
      const msg = 'Dettatura vocale non supportata.';
      setState(s => ({ ...s, error: msg }));
      onErrorRef.current?.(msg);
      return;
    }

    if (isNative) {
      if (!state.isModelReady) {
        const msg = 'Modello vocale in caricamento — attendi qualche secondo';
        setState(s => ({ ...s, error: msg }));
        onErrorRef.current?.(msg);
        return;
      }

      cleanupNative();
      setState(s => ({ ...s, isListening: true, error: null, transcript: '' }));

      // Timeout di sicurezza 15s
      timeoutRef.current = setTimeout(() => {
        const msg = 'Nessun risultato — riprova';
        cleanupNative();
        VoskSpeech.stop();
        setState(s => ({ ...s, isListening: false, error: msg }));
        onErrorRef.current?.(msg);
      }, 15000);

      errorRef.current = await VoskSpeech.addListener('recognitionError', (_data: any) => {
        const msg = 'Errore riconoscimento vocale — riprova';
        cleanupNative();
        VoskSpeech.stop();
        setState(s => ({ ...s, isListening: false, error: msg }));
        onErrorRef.current?.(msg);
      });

      stateRef.current = await VoskSpeech.addListener('listeningState', (data: any) => {
        if (data?.status === 'stopped') {
          const text = lastTextRef.current;
          cleanupNative();
          setState(s => ({ ...s, transcript: text, isListening: false }));
          if (text) onResultRef.current?.(text);
        }
      });

      partialRef.current = await VoskSpeech.addListener('partialResults', (data: any) => {
        const text = data.matches?.[0] ?? '';
        if (!text) return;
        lastTextRef.current = text;
        setState(s => ({ ...s, transcript: text }));
      });

      try {
        await VoskSpeech.start();
      } catch (e: any) {
        const msg = e?.message ?? 'Impossibile avviare il riconoscimento vocale';
        cleanupNative();
        setState(s => ({ ...s, isListening: false, error: msg }));
        onErrorRef.current?.(msg);
      }

    } else {
      const recognition = new SpeechRecognitionAPI();
      recognition.lang = lang;
      recognition.continuous = continuous;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart  = () => setState(s => ({ ...s, isListening: true, error: null, transcript: '' }));
      recognition.onresult = (event: any) => {
        const text = event.results[event.results.length - 1][0].transcript.trim();
        setState(s => ({ ...s, transcript: text, isListening: false }));
        onResultRef.current?.(text);
      };
      recognition.onerror = (event: any) => {
        let msg = 'Errore riconoscimento vocale';
        switch (event.error) {
          case 'network':       msg = 'Connessione necessaria per la dettatura vocale'; break;
          case 'not-allowed':   msg = 'Accesso microfono negato — verifica permessi';   break;
          case 'no-speech':     msg = 'Nessun parlato rilevato — riprova';              break;
          case 'audio-capture': msg = 'Microfono non trovato o non funzionante';        break;
        }
        setState(s => ({ ...s, isListening: false, error: msg }));
        onErrorRef.current?.(msg);
      };
      recognition.onend = () => setState(s => ({ ...s, isListening: false }));

      recognitionRef.current = recognition;
      recognition.start();
    }
  }, [state.isSupported, state.isModelReady, lang, continuous, cleanupNative]);

  const stopListening = useCallback(() => {
    if (isNative) {
      const text = lastTextRef.current;
      cleanupNative();
      VoskSpeech.stop();
      setState(s => ({ ...s, isListening: false }));
      if (text) onResult?.(text);
    } else {
      recognitionRef.current?.stop();
      setState(s => ({ ...s, isListening: false }));
    }
  }, [cleanupNative]);

  const clearError = useCallback(() => setState(s => ({ ...s, error: null })), []);

  return { ...state, startListening, stopListening, clearError };
}
